import React, { useMemo, useContext } from 'react';
import { OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { Check } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import useTradingRulesViolationsOverTime from '../../hooks/useTradingRulesViolationsOverTime';

const RED_ALERT = '#ef4444';
const DOT_TIP = { green: 'No violations', red: 'Rule violation', neutral: 'Before account created', pending: 'Loading…' };

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** First weekday on or after account creation (local calendar). */
function getFirstTrackingDay(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return toDateStr(d);
}

function getLastTradingDays(n) {
  const days = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (days.length < n) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      days.unshift(toDateStr(d));
    }
    d.setDate(d.getDate() - 1);
  }
  return days;
}

function classifyDay(dateStr, violationsData, isLoaded, firstTrackingDay) {
  if (!isLoaded) return { status: 'pending', count: 0 };
  if (firstTrackingDay && dateStr < firstTrackingDay) {
    return { status: 'neutral', count: 0 };
  }
  const bucket = (violationsData || []).find((b) => b.timestamp?.startsWith(dateStr));
  if (!bucket || bucket.total === 0) return { status: 'green', count: 0 };
  return { status: 'red', count: bucket.total };
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

function WeekDayStrip({ displayStatuses, colors }) {
  const dayColumnStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    minWidth: 22,
  };

  const markerSlotStyle = {
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 1,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      {displayStatuses.map(({ date, status, count }) => (
        <div key={date} style={dayColumnStyle} title={`${dayLabel(date)} — ${DOT_TIP[status]}${count > 0 ? ` (${count})` : ''}`}>
          <span style={markerSlotStyle}>
            {status === 'pending' ? null : status === 'green' ? (
              <Check size={14} style={{ color: '#22c55e', display: 'block' }} />
            ) : status === 'red' ? (
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: RED_ALERT,
                lineHeight: 1,
                fontFamily: 'monospace',
              }}>
                {count}
              </span>
            ) : (
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: colors.textMuted,
                lineHeight: 1,
              }}>
                —
              </span>
            )}
          </span>
          <span style={{ fontSize: '0.62rem', color: colors.textMuted, whiteSpace: 'nowrap', lineHeight: 1 }}>
            {dayLabel(date)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * StreakPill — clean-day streak indicator.
 *
 * Default: fire + last 5 trading days (Account Health).
 * `compact` (header): fire + count only; hover/tap opens last-week detail.
 */
const StreakPill = ({ account: accountProp, compact = false } = {}) => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const account = accountProp ?? (selectedAccount ? { AccountID: selectedAccount.AccountID } : null);

  const { data: violationsData, loading } = useTradingRulesViolationsOverTime(account, '30days');
  const isLoaded = !loading;

  const firstTrackingDay = useMemo(() => {
    const anchor = user?.createdAt || user?.legalAcceptance?.tosAcceptedAt;
    return getFirstTrackingDay(anchor);
  }, [user?.createdAt, user?.legalAcceptance?.tosAcceptedAt]);

  const displayDays = useMemo(() => getLastTradingDays(5), []);
  const extendedDays = useMemo(() => getLastTradingDays(30), []);

  const displayStatuses = useMemo(
    () => displayDays.map((d) => ({ date: d, ...classifyDay(d, violationsData, isLoaded, firstTrackingDay) })),
    [displayDays, violationsData, isLoaded, firstTrackingDay]
  );

  const cleanStreak = useMemo(() => {
    if (!isLoaded) return 0;
    let count = 0;
    for (let i = extendedDays.length - 1; i >= 0; i--) {
      const { status } = classifyDay(extendedDays[i], violationsData, isLoaded, firstTrackingDay);
      if (status === 'neutral') continue;
      if (status === 'green') count++;
      else break;
    }
    return count;
  }, [extendedDays, violationsData, isLoaded, firstTrackingDay]);

  const hasStreak = isLoaded && cleanStreak > 0;
  const fireSummary = hasStreak
    ? `${cleanStreak}-day clean streak — no rule violations`
    : isLoaded
      ? 'No clean streak yet'
      : 'Loading streak…';

  const fireNode = (
    <div
      role={compact ? 'button' : undefined}
      tabIndex={compact ? 0 : undefined}
      aria-label={fireSummary}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 2 : 3,
        lineHeight: 1,
        marginTop: compact ? 0 : 5,
        cursor: compact ? 'pointer' : 'default',
        userSelect: 'none',
        padding: compact ? '2px 0' : 0,
      }}
    >
      <span
        style={{
          fontSize: compact ? '0.9rem' : '1rem',
          filter: hasStreak ? 'none' : 'grayscale(1) brightness(0.7)',
          opacity: hasStreak ? 1 : 0.75,
          lineHeight: 1,
        }}
        aria-hidden
      >
        🔥
      </span>
      <span
        style={{
          fontSize: compact ? '0.72rem' : '0.75rem',
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: hasStreak ? colors.text : colors.textMuted,
        }}
      >
        {isLoaded ? cleanStreak : '—'}
      </span>
    </div>
  );

  if (compact) {
    const popover = (
      <Popover
        id="streak-week-popover"
        style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          maxWidth: 280,
        }}
      >
        <Popover.Body style={{ padding: '10px 12px', color: colors.text }}>
          <div
            style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              marginBottom: 8,
            }}
          >
            Last week
          </div>
          <WeekDayStrip displayStatuses={displayStatuses} colors={colors} />
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: 10, lineHeight: 1.35 }}>
            {fireSummary}
          </div>
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger
        placement="bottom"
        trigger={['hover', 'focus', 'click']}
        rootClose
        overlay={popover}
      >
        {fireNode}
      </OverlayTrigger>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'default', userSelect: 'none' }}>
      {displayStatuses.map(({ date, status, count }) => (
        <OverlayTrigger
          key={date}
          placement="bottom"
          overlay={<Tooltip>{dayLabel(date)} — {DOT_TIP[status]}{count > 0 ? ` (${count})` : ''}</Tooltip>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 18 }}>
            <span style={{
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              lineHeight: 1,
            }}>
              {status === 'pending' ? null : status === 'green' ? (
                <Check size={14} style={{ color: '#22c55e', display: 'block' }} />
              ) : status === 'red' ? (
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: RED_ALERT,
                  lineHeight: 1,
                  fontFamily: 'monospace',
                }}>
                  {count}
                </span>
              ) : (
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: colors.textMuted,
                  lineHeight: 1,
                }}>
                  —
                </span>
              )}
            </span>
            <span style={{ fontSize: '0.58rem', color: colors.textMuted, whiteSpace: 'nowrap', lineHeight: 1 }}>
              {dayLabel(date)}
            </span>
          </div>
        </OverlayTrigger>
      ))}

      <OverlayTrigger placement="bottom" overlay={<Tooltip>{fireSummary}</Tooltip>}>
        {fireNode}
      </OverlayTrigger>
    </div>
  );
};

export default StreakPill;
